import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { PromoTargetType } from '@masters-club/types';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { getCourseTitle, type Course } from '@/resources/courses/types';
import { getChannelPassName, type ChannelPass } from '@/resources/channel-passes/types';
import {
  getSubscriptionPlanName,
  type SubscriptionPlan,
} from '@/resources/subscription-plans/types';
import { getBookTitle, type Book } from '@/resources/books/types';
import type { PromoCodeItem } from './types';

interface PromoTargetMultiSelectProps {
  /** Не-ALL целевой тип. Для ALL компонент не рендерится в parent. */
  targetType: Exclude<PromoCodeItem['targetType'], typeof PromoTargetType.ALL>;
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

const PLACEHOLDER_BY_TARGET: Record<PromoTargetMultiSelectProps['targetType'], string> = {
  [PromoTargetType.COURSE]: 'Выберите курсы…',
  [PromoTargetType.CHANNEL_PASS]: 'Выберите подписки в канал…',
  [PromoTargetType.SUBSCRIPTION_PLAN]: 'Выберите тарифные планы…',
  [PromoTargetType.BOOK]: 'Выберите книги…',
  [PromoTargetType.PRICING_TIER]: 'Выберите тарифы…',
};

function CoursesSelect(props: Omit<PromoTargetMultiSelectProps, 'targetType'>) {
  const { result, query } = useList<Course>({
    resource: 'courses',
    pagination: { pageSize: 200 },
  });
  const options = useMemo<MultiSelectOption[]>(
    () =>
      (result?.data ?? []).map((row) => ({
        value: String(row.id),
        label: `${getCourseTitle(row, 'ru')} (#${row.id})`,
      })),
    [result?.data],
  );
  return renderSelect(PromoTargetType.COURSE, options, props, query.isLoading);
}

function ChannelPassesSelect(props: Omit<PromoTargetMultiSelectProps, 'targetType'>) {
  const { result, query } = useList<ChannelPass>({
    resource: 'channel-passes',
    pagination: { pageSize: 200 },
  });
  const options = useMemo<MultiSelectOption[]>(
    () =>
      (result?.data ?? []).map((row) => ({
        value: String(row.id),
        label: `${getChannelPassName(row, 'ru')} (#${row.id})`,
      })),
    [result?.data],
  );
  return renderSelect(PromoTargetType.CHANNEL_PASS, options, props, query.isLoading);
}

function SubscriptionPlansSelect(props: Omit<PromoTargetMultiSelectProps, 'targetType'>) {
  const { result, query } = useList<SubscriptionPlan>({
    resource: 'subscription-plans',
    pagination: { pageSize: 200 },
  });
  const options = useMemo<MultiSelectOption[]>(
    () =>
      (result?.data ?? []).map((row) => ({
        value: String(row.id),
        label: `${getSubscriptionPlanName(row, 'ru')} (#${row.id})`,
      })),
    [result?.data],
  );
  return renderSelect(PromoTargetType.SUBSCRIPTION_PLAN, options, props, query.isLoading);
}

function BooksSelect(props: Omit<PromoTargetMultiSelectProps, 'targetType'>) {
  const { result, query } = useList<Book>({
    resource: 'books',
    pagination: { pageSize: 200 },
  });
  const options = useMemo<MultiSelectOption[]>(
    () =>
      (result?.data ?? []).map((row) => ({
        value: String(row.id),
        label: `${getBookTitle(row, 'ru')} (#${row.id})`,
      })),
    [result?.data],
  );
  return renderSelect(PromoTargetType.BOOK, options, props, query.isLoading);
}

function renderSelect(
  targetType: PromoTargetMultiSelectProps['targetType'],
  options: MultiSelectOption[],
  props: Omit<PromoTargetMultiSelectProps, 'targetType'>,
  isLoading: boolean,
) {
  const stringValue = props.value.map(String);
  return (
    <MultiSelect
      options={options}
      value={stringValue}
      onChange={(next) =>
        props.onChange(next.map((v) => Number(v)).filter((n) => Number.isFinite(n)))
      }
      placeholder={isLoading ? 'Загрузка…' : PLACEHOLDER_BY_TARGET[targetType]}
      searchPlaceholder="Поиск по названию…"
      emptyMessage={isLoading ? 'Загрузка…' : 'Ничего не найдено'}
      disabled={props.disabled === true || isLoading}
    />
  );
}

export function PromoTargetMultiSelect({
  targetType,
  ...rest
}: PromoTargetMultiSelectProps) {
  if (targetType === PromoTargetType.COURSE) {
    return <CoursesSelect {...rest} />;
  }
  if (targetType === PromoTargetType.CHANNEL_PASS) {
    return <ChannelPassesSelect {...rest} />;
  }
  if (targetType === PromoTargetType.SUBSCRIPTION_PLAN) {
    return <SubscriptionPlansSelect {...rest} />;
  }
  return <BooksSelect {...rest} />;
}
